import { defaults } from 'lodash';
import { Subscription } from 'rxjs';
import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '../../injection';

import { OrganizationMembership } from '../data/organization-membership.do';
import { User } from '../data/user.do';
import type { IMobizZone } from '../other/mobiz-zone';
import { MOBIZ_ZONE } from '../other/mobiz-zone';
import { CONFIGURATION_SERVICE, IConfigurationService } from '../services/configuration.interface';
import { IMobizStateService } from '../services/mobiz-state.interface';

import { ILogClientContext, ILogContext, ILogOrganizationContext, ILogUserContext } from './log-context.interface';
import { defaultOrganizationContext, defaultUserContext } from './log-default-context';
import { ILogHandler, ILogHandlerStatus } from './log-handler.interface';
import { LogLevel } from './log-level.enum';
import { ILogTrackingInfo } from './log-tracking-info.interface';

/**
 * The LOG_HANDLER injection token is a multi-value injection token that can be used to provide multiple log handlers.
 *
 * Each log handler can choose which logs to handle and has the option to return tracking details which will be
 * displayed to the user in certain situations.
 */
export const LOG_HANDLER: InjectionToken<ILogHandler[]>
  = new InjectionToken<ILogHandler[]>('ILogHandler[]');

@Injectable()
export class LoggerService implements OnDestroy {
  private currentUserContext: ILogUserContext | null = null;
  private currentOrganizationContext: ILogOrganizationContext | null = null;
  private currentUserSubscription: Subscription;
  private currentMembershipSubscription: Subscription;

  private readonly enabledHandlers: ILogHandler[];

  constructor(
    @Inject(CONFIGURATION_SERVICE)
    private readonly configurationService: IConfigurationService,
    @Inject(IMobizStateService)
    private readonly stateService: IMobizStateService,
    @Inject(LOG_HANDLER)
    private readonly logHandlers: ILogHandler[],
    @Inject(MOBIZ_ZONE) @Optional()
    private readonly mobizZone: IMobizZone | null,
  ) {
    this.enabledHandlers = this.initLoggerHandlers();
    if (this.mobizZone === null && window.hasOwnProperty('ng')) {
      throw new Error('The MOBIZ_ZONE DI token is required for Angular applications.');
    }
  }

  public ngOnDestroy(): void {
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
    }
    if (this.currentMembershipSubscription) {
      this.currentMembershipSubscription.unsubscribe();
    }
  }

  public async getStatus(): Promise<ILogHandlerStatus[]> {
    const logHandlers: ILogHandler[] = [...this.logHandlers];

    return Promise.all(logHandlers.map(async (service: ILogHandler): Promise<ILogHandlerStatus> => {
      return service.getStatus();
    }));
  }

  /**
   * Submit logs to enabled ILogHandler instances provided by the multi-value LOG_HANDLER injection
   * token.
   *
   * NOTE: When MOBIZ_ZONE is provided, this method is executed outside of the default zone.js zone.
   *
   * @see LOG_HANDLER
   * @see NgZone
   */
  public log(level: LogLevel, message: string, context?: ILogContext): void {
    this.logPromise(level, message, context)
      .then((): void => {
        // Nothing to do.
      });
  }

  /**
   * Submit logs to enabled ILogHandler instances provided by the multi-value LOG_HANDLER injection
   * token.
   *
   * NOTE: When MOBIZ_ZONE is provided, this method is executed outside of the default zone.js zone.
   *
   * @see LOG_HANDLER
   * @see NgZone
   */
  public async logPromise(level: LogLevel, message: string, context?: ILogContext): Promise<ILogTrackingInfo[]> {
    if (this.mobizZone) {
      // Handle logs outside the Angular zone.
      const logTrackingInfoList: ILogTrackingInfo[] = await this.mobizZone.runOutsideZone(
        async (): Promise<ILogTrackingInfo[]> => this.doLog(level, message, context)
      );
      // Re-enter the Angular zone and return the log tracking information.
      return this.mobizZone.runInsideZone(
        async (): Promise<ILogTrackingInfo[]> => Promise.resolve(logTrackingInfoList)
      );
    }
    return this.doLog(level, message, context);
  }

  private async doLog(
    level: LogLevel,
    message: string,
    providedContext?: ILogContext,
  ): Promise<ILogTrackingInfo[]> {
    const urlContext: Record<string, string> = {
      url: window.location.toString(),
    };

    const clientContext: ILogClientContext = {
      clientId: this.configurationService.clientId,
      clientVersion: this.configurationService.clientVersion,
    };

    const fullContext: ILogContext = defaults(
      clientContext,
      this.currentUserContext,
      this.currentOrganizationContext,
      providedContext,
      urlContext,
    );

    return Promise.all(this.enabledHandlers.map(async (service: ILogHandler): Promise<ILogTrackingInfo | null> => {
      return service.handle(level, message, fullContext);
    })).then((infoList: (ILogTrackingInfo | null)[]): ILogTrackingInfo[] => {
      return infoList.filter((info: ILogTrackingInfo) => !!info) as ILogTrackingInfo[];
    });
  }

  private initLoggerHandlers(): ILogHandler[] {
    // Store current user and organization context in the logger service to improve performance.
    this.currentUserSubscription = this.stateService.currentUser.asObservable()
      .subscribe((user: User): void => {
        const userInfo: ILogUserContext = { ...defaultUserContext };
        if (user) {
          userInfo.userId = user.id;
          userInfo.userName = user.fullName;
        }
        this.currentUserContext = userInfo;
      });

    this.currentMembershipSubscription = this.stateService.currentMembership.asObservable()
      .subscribe((membership: OrganizationMembership): void => {
        const membershipInfo: ILogOrganizationContext = { ...defaultOrganizationContext };
        if (membership) {
          membershipInfo.organizationId = membership.organization.id;
          membershipInfo.organizationName = membership.organization.fullName;
        }
        this.currentOrganizationContext = membershipInfo;
      });

    const availableLogHandlers: ILogHandler[] = [...this.logHandlers];
    return availableLogHandlers.filter((logHandler: ILogHandler): boolean => {
      return logHandler.enabled;
    });
  }
}
